import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from '@apollo/react-hooks'
import { chatMutations } from '../../graphql/mutations'
import { chatQueries } from '../../graphql/queries'
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import Spinner from "ui-components/lib/components/Spinner";
import useAlertStack from 'ui-components/lib/hooks/useAlertStack'
import { formatISOTimestampToDate } from "../../utils";
import { ReactComponent as SendMessageIcon } from "../../images/icons/sendMessageIcon.svg";
import { ReactComponent as CloseIcon } from "../../images/icons/close_icon.svg";
import { FaPaperclip } from 'react-icons/fa'
import Dropzone from '../Dropzone'
import "./ChatDemand.css";

const CHAT_MODAL_DROPZONE_ID = 'file-chat-demand-modal'

const ChatDemand = (props) =>{
  const { showMessage } = useAlertStack()
  const [message, setMessage] = useState("")
  const [chatMessagesData, setChatMessagesData] = useState([])
  const [chatFiles, setChatFiles] = useState([])
  const [loadingFiles, setLoadingFiles] = useState(false)
  const messagesEndRef = useRef(null)

  const {
    handleClose,
    openModal,
    demandId,
    demandLawyer,
    subscription
  } = props;

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'var(--card-bg)',
    border: 'none !important',
    boxShadow: 24,
    outline: 'none',
  };

  const { loading: chatLoading, data: chatData, refetch: chatRefetch } = useQuery(chatQueries.GET_CHAT_MESSAGES, {
    variables: {
      demandId: demandId,
      subscription: subscription
    },
  })

  const [sendChatMessage, { loading: sendChatMessageLoading }] = useMutation(chatMutations.SEND_CHAT_MESSAGES, {
    onCompleted() {
      setMessage("")
      setChatFiles([])
      chatRefetch()
    },
    onError() {
      showMessage({
        title: 'Erro ao enviar a mensagem',
        message: 'Tente novamente mais tarde.',
        color: 'danger',
        position: 'bottom-left',
        time: 4000,
      })
    },
  })

  const handleChatFiles = (file) => {
    setLoadingFiles(false)
    const actualFilesList = file.map(({ fileName: name, randomFileName, mimeType, fileExtension: extension, fileSize: size }) => ({
      attachment: '',
      name,
      randomFileName,
      mimeType,
      extension,
      size,
    }))
    setChatFiles(actualFilesList)
  }

  const handleChangeMessage = (event) => {
    setMessage(event.target.value)
  }

  const canSend = message.trim().length > 0 || chatFiles.length > 0

  const handleSendMessage = () => {
    if (canSend) {
      sendChatMessage({
        variables: {
          demandId: demandId ? demandId : null,
          lawyerId: demandLawyer ? demandLawyer._id : null,
          message: message,
          subscription: subscription,
          files: chatFiles,
        },
      })
    }
  }

  const handleSendMessageByKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      if (canSend) {
        handleSendMessage()
      }
    }
  }

  const openFilePicker = (e) => {
    e.preventDefault()
    const input = document.getElementById(CHAT_MODAL_DROPZONE_ID)
    if (input) input.click()
  }

  useEffect(() => {
    if(chatData?.getChatMessages) {
      const filteredChatMessages = chatData?.getChatMessages.find(item => item.sender === "lawyer")

      const chatMessages = chatData?.getChatMessages.map((item, index) => {
        let messageCreatedAtDate = formatISOTimestampToDate(item.createdAt).formattedDate
        let messageCreatedAtHour = formatISOTimestampToDate(item.createdAt).formattedHourMinute

        if(!messageCreatedAtDate && !messageCreatedAtHour) {
          messageCreatedAtDate = formatISOTimestampToDate(item.created_at, 'ISO').formattedDate
          messageCreatedAtHour = formatISOTimestampToDate(item.created_at, 'ISO').formattedHourMinute
        }

        return {
          _id: item._id,
          chatId: item.chatId,
          message: item.message,
          sender: item.sender,
          files: item.files,
          createdAtDate: messageCreatedAtDate,
          createdAtHour: messageCreatedAtHour
        }
      })

      if(!filteredChatMessages){
        chatMessages.push({
          _id: 'welcome-placeholder',
          message: `Olá Bem-vindo à B.onuz. Envie sua mensagem e aguarde um momento que ${subscription ? `o advogado especialista` : `um dos nossos advogados especialistas`} já irá te atender.`,
          sender: 'lawyer',
          files: [],
          createdAtDate: false,
          createdAtHour: false
        })
      }
      
      setChatMessagesData(chatMessages)
    }
  }, [chatData]); // eslint-disable-line 

  useEffect(() => {
    if(openModal){
      chatRefetch()
    }
  },[openModal]) // eslint-disable-line

  useEffect(() => {
    if (!openModal) return
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [openModal, chatMessagesData])

  const inputBusy = chatLoading || sendChatMessageLoading || loadingFiles

  return (
    <Modal
      open={openModal}
      onClose={handleClose}
      disableAutoFocus
      sx={{zIndex: '9999'}}
    >
      <Box sx={style} className="cdm-root">
        <Box className="cdm-header">
            <Box className="chat-lawyer-name">
              {demandLawyer ? (demandLawyer.displayAs) : ('Chat')}
            </Box>
            <Box className="chat-button-close">
            <button type="button" onClick={() => handleClose()}>
              <CloseIcon className="close-icon-chat" />Fechar
            </button>
            </Box>
        </Box>
        <Box className="cdm-messages">
            <Box className="cdm-messages-inner">
              {chatMessagesData ? (
                chatMessagesData.map((item, index) => (
                  <React.Fragment key={item._id || `msg-${index}`}>
                    {item.createdAtDate && (
                      <Box className="message-createdAt">
                        {item.createdAtDate}
                      </Box>
                    )}
                    {item.sender === 'client' ? (
                      <Box className="message-client-container">
                        <Box className="message-client">
                          {item.message}
                          {item.files && item.files.length > 0 && item.files.map((file, fileIndex) => (
                            <div key={file._id || fileIndex}>
                              <button
                                type="button"
                                className="message-attachment-link"
                                onClick={() => window.open(file.fileURL, '_blank')}
                              >
                                <FaPaperclip size={12} />
                                {file.name}
                              </button>
                            </div>
                          ))}
                        </Box>
                        {item.createdAtHour}
                      </Box>
                    ) : (
                      <Box className="message-lawyer-container">
                        {item.createdAtHour}
                        <Box className="message-lawyer">
                          {item.message}
                          {item.files && item.files.length > 0 && item.files.map((file, fileIndex) => (
                            <div key={file._id || fileIndex}>
                              <button
                                type="button"
                                className="message-attachment-link"
                                onClick={() => window.open(file.fileURL, '_blank')}
                              >
                                <FaPaperclip size={12} />
                                {file.name}
                              </button>
                            </div>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <Box className="message-lawyer">
                  Você não possui nenhuma mensagem para esta demanda.
                </Box>
              )}
              <div ref={messagesEndRef} aria-hidden />
            </Box>
        </Box>
        <Box className="cdm-footer">
            <FormControl variant="outlined">
              <OutlinedInput
                multiline
                minRows={1}
                maxRows={4}
                sx={{
                  background: chatLoading ? 'var(--table-row-zebra)' : 'transparent',
                    borderRadius: "8px", 
                    border: "1px solid", 
                  borderColor: "var(--border-color)",
                  color: "var(--text-color)",
                  alignItems: 'flex-end',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--border-color)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--border-color)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--purple-bnz)',
                  },
                  }}
                id="outlined-adornment-message"
                value={message}
                onChange={handleChangeMessage}
                onKeyDown={handleSendMessageByKeyDown}
                disabled={inputBusy}
                startAdornment={
                  <InputAdornment position="start">
                    <IconButton
                      onClick={openFilePicker}
                      edge="start"
                      disabled={inputBusy}
                      aria-label="Anexar arquivo"
                      size="small"
                    >
                      <FaPaperclip size={18} />
                    </IconButton>
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    {inputBusy ? ( 
                      <Spinner color="primary" /> 
                    ) : (
                      <IconButton
                        onClick={handleSendMessage}
                        disabled={!canSend}
                        aria-label="Enviar mensagem"
                      >
                        <SendMessageIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                }
                placeholder="Escreva uma mensagem"
                inputProps={{
                  'aria-label': 'message',
                }}
              />
            </FormControl>
            {chatFiles.length > 0 && (
              <Box className="chat-modal-file-chips">
                {chatFiles.map((file, index) => (
                  <span key={`${file.randomFileName || file.name}-${index}`} className="chat-modal-file-chip">
                    {file.name}
                  </span>
                ))}
              </Box>
            )}
            <Dropzone
              onLoad={setLoadingFiles}
              onComplete={(uploaded) => handleChatFiles(uploaded)}
              id={CHAT_MODAL_DROPZONE_ID}
              classe="dropzone-hide"
            />
        </Box>
      </Box>
    </Modal>
  );
}

export default ChatDemand;
